import React from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import { withCdn } from "../blogTheme";
import { BlogImage } from "./BlogImage";

function formatBytes(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function MediaCard({ media, onDelete, onCopyUrl, isDeleting }) {
  const uploaderName = media.uploadedBy
    ? media.uploadedBy.firstName && media.uploadedBy.lastName
      ? `${media.uploadedBy.firstName} ${media.uploadedBy.lastName}`
      : media.uploadedBy.username || "Admin"
    : null;

  const handleCopy = () => {
    navigator.clipboard.writeText(withCdn(media.url)).then(() => toast.success("URL copied"));
    if (onCopyUrl) onCopyUrl(media.url);
  };

  return (
    <Box
      sx={{
        borderRadius: "16px",
        border: "1px solid #EBEBEB",
        overflow: "hidden",
        bgcolor: "#fff",
        boxShadow: "0 1px 6px rgba(0,0,0,0.05)",
        opacity: isDeleting ? 0.5 : 1,
        pointerEvents: isDeleting ? "none" : "auto",
        transition: "opacity 0.2s, box-shadow 0.2s",
        display: "flex",
        flexDirection: "column",
        "&:hover": { boxShadow: "0 4px 16px rgba(0,0,0,0.10)" },
      }}
    >
      <BlogImage
        src={withCdn(media.url)}
        alt={media.alt || media.originalName}
        width="100%"
        height={148}
        borderRadius="0"
        objectFit="contain"
        sx={{ bgcolor: "#F9FAFB" }}
      />

      <Box sx={{ p: 1.5, display: "flex", flexDirection: "column", gap: 0.5, flex: 1 }}>
        <Tooltip title={media.originalName}>
          <Typography
            variant="caption"
            noWrap
            display="block"
            sx={{ fontFamily: "Heebo", fontWeight: 600, color: "#362B24", lineHeight: 1.4 }}
          >
            {media.originalName}
          </Typography>
        </Tooltip>

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography
            variant="caption"
            sx={{ fontFamily: "Heebo", color: "#6B7280", fontSize: "0.7rem" }}
          >
            {formatBytes(media.size)}
            {uploaderName && ` · ${uploaderName}`}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, ml: 0.5 }}>
            <Tooltip title="Copy URL">
              <IconButton
                size="small"
                onClick={handleCopy}
                sx={{ p: 0.5, "&:hover": { bgcolor: "rgba(6,95,70,0.08)" } }}
              >
                <ContentCopyIcon sx={{ fontSize: 15, color: "#065F46" }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton
                size="small"
                onClick={() => onDelete && onDelete(media._id)}
                disabled={isDeleting}
                sx={{ p: 0.5, "&:hover": { bgcolor: "rgba(234,0,27,0.08)" } }}
              >
                {isDeleting
                  ? <CircularProgress size={14} sx={{ color: "#065F46" }} />
                  : <DeleteIcon sx={{ fontSize: 15, color: "#EA001B" }} />}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
